import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useApp } from '@/context/AppContext'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import { setToken, getToken } from '@/utils/auth'
import {
  getBiometryInfo, authenticate as bioAuthenticate,
  isBiometricEnabled, enableBiometric, disableBiometric,
  biometryLabel, biometryIcon,
} from '@/utils/biometric'

// ── Post-login enrollment prompt ─────────────────────────────────────────────
function BiometricEnrollPrompt({ biometryType, onAccept, onSkip }) {
  const label = biometryLabel(biometryType)
  const icon  = biometryIcon(biometryType)
  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/50" onClick={onSkip}>
      <div className="w-full bg-white rounded-t-3xl p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="w-12 h-1 bg-slate-200 rounded-full mx-auto mb-5" />
        <div className="text-center mb-5">
          <span className="text-5xl">{icon}</span>
          <h2 className="text-xl font-bold text-slate-800 mt-3">Enable {label}?</h2>
          <p className="text-base text-slate-500 mt-1 leading-relaxed">
            Sign in faster and more securely next time using {label} — no password needed.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <button
            onClick={onAccept}
            className="w-full py-4 rounded-2xl bg-brand-700 text-white font-bold text-base"
          >
            Enable {label}
          </button>
          <button
            onClick={onSkip}
            className="w-full py-3 text-slate-500 text-base font-medium"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main login screen ─────────────────────────────────────────────────────────
export default function Login() {
  const navigate = useNavigate()
  const { loadUserFromApi } = useApp()

  const [form, setForm]           = useState({ email: '', password: '' })
  const [error, setError]         = useState(null)
  const [loading, setLoading]     = useState(false)
  const [bioInfo, setBioInfo]     = useState({ available: false, type: 'none' })
  const [bioLoading, setBioLoading] = useState(false)
  const [showEnroll, setShowEnroll] = useState(false)   // post-login enrollment prompt
  const [pendingUser, setPendingUser] = useState(null)  // user data waiting for enrollment decision

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  // Check biometric availability and auto-trigger if enabled
  useEffect(() => {
    let cancelled = false
    async function init() {
      const info = await getBiometryInfo()
      if (cancelled) return
      setBioInfo(info)

      // Auto-trigger biometric if user has opted in and a token exists
      if (info.available && isBiometricEnabled() && getToken()) {
        handleBiometricLogin(info.type)
      }
    }
    init()
    return () => { cancelled = true }
  }, [])

  // ── Biometric login ──────────────────────────────────────────────────────
  async function handleBiometricLogin(type = bioInfo.type) {
    setBioLoading(true)
    setError(null)
    try {
      const success = await bioAuthenticate(`Sign in to GoodGuardian with ${biometryLabel(type)}`)
      if (success) {
        // Token already in localStorage — just reload the user profile
        await loadUserFromApi()
        navigate('/dashboard', { replace: true })
      } else {
        setError('Biometric authentication cancelled. Use your password instead.')
      }
    } catch {
      setError('Biometric authentication failed. Use your password instead.')
    } finally {
      setBioLoading(false)
    }
  }

  // ── Password login ───────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e?.preventDefault()
    if (!form.email || !form.password) return
    setError(null)
    setLoading(true)
    try {
      const res  = await fetch('/api/auth/login', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: form.email, password: form.password }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Login failed.')

      setToken(data.token)

      // Offer biometric enrollment if available and not yet enabled
      if (bioInfo.available && !isBiometricEnabled()) {
        setPendingUser(data.user)
        setShowEnroll(true)
      } else {
        await loadUserFromApi(data.user)
        navigate('/dashboard', { replace: true })
      }
    } catch (err) {
      setError(err.message.includes('Invalid') ? 'Incorrect email or password.' : err.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleEnrollAccept() {
    enableBiometric()
    setShowEnroll(false)
    await loadUserFromApi(pendingUser)
    navigate('/dashboard', { replace: true })
  }

  async function handleEnrollSkip() {
    setShowEnroll(false)
    await loadUserFromApi(pendingUser)
    navigate('/dashboard', { replace: true })
  }

  const bioEnabled = isBiometricEnabled()
  const showBioButton = bioInfo.available && bioEnabled && getToken()
  const bioLabel = biometryLabel(bioInfo.type)
  const bioIcon  = biometryIcon(bioInfo.type)

  return (
    <div className="flex flex-col h-full bg-white lg:bg-slate-100 lg:px-6 lg:py-10">
      <div className="flex flex-col h-full bg-white lg:max-w-md lg:mx-auto lg:w-full lg:rounded-3xl lg:overflow-hidden lg:shadow-xl lg:border lg:border-slate-200">

        {/* Header */}
        <div className="brand-gradient px-5 pb-8 flex flex-col items-center gap-3" style={{ paddingTop: 'calc(1.5rem + env(safe-area-inset-top, 0px))' }}>
          <span className="text-4xl">🛡️</span>
          <h1 className="text-white text-2xl font-extrabold">GoodGuardian</h1>
          <p className="text-blue-100 text-sm">Welcome back</p>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-8 flex flex-col gap-5">

          {/* Face ID / Touch ID quick-login button */}
          {showBioButton && (
            <button
              type="button"
              onClick={() => handleBiometricLogin()}
              disabled={bioLoading}
              className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl bg-brand-50 border-2 border-brand-200 text-brand-700 font-bold text-base active:scale-[0.98] transition-transform disabled:opacity-50"
            >
              {bioLoading ? (
                <span className="w-5 h-5 border-2 border-brand-700 border-t-transparent rounded-full animate-spin" />
              ) : (
                <span className="text-2xl">{bioIcon}</span>
              )}
              {bioLoading ? 'Verifying…' : `Sign in with ${bioLabel}`}
            </button>
          )}

          {/* Divider (only shown when biometric button is visible) */}
          {showBioButton && (
            <div className="flex items-center gap-3 -my-1">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-sm text-slate-600 font-medium">or use password</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>
          )}

          <div className="flex flex-col gap-4">
            <Input
              label="Email address"
              type="email"
              value={form.email}
              onChange={e => set('email', e.target.value)}
              placeholder="your@email.com"
              required
              autoComplete="email"
            />
            <Input
              label="Password"
              type="password"
              value={form.password}
              onChange={e => set('password', e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />
            <div className="flex justify-end -mt-2">
              <Link to="/forgot-password" className="text-sm text-brand-700 font-semibold hover:underline">
                Forgot password?
              </Link>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <Button
            type="submit"
            variant="primary" size="lg" fullWidth
            loading={loading}
            disabled={!form.email || !form.password}
          >
            Sign In
          </Button>

          <div className="text-center">
            <p className="text-sm text-slate-500">
              Don't have an account?{' '}
              <Link to="/register" className="text-brand-700 font-semibold hover:underline">
                Create one
              </Link>
            </p>
          </div>

          <div className="disclaimer text-xs">
            ⚠️ GoodGuardian provides legal education, not legal advice. All AI output
            requires attorney review before any legal action.
          </div>
        </form>
      </div>

      {/* Biometric enrollment prompt (shown once after first password login) */}
      {showEnroll && (
        <BiometricEnrollPrompt
          biometryType={bioInfo.type}
          onAccept={handleEnrollAccept}
          onSkip={handleEnrollSkip}
        />
      )}
    </div>
  )
}
